// ============ DIFFICULTY SETTINGS ============
export const DIFFICULTY = {
  easy: {
    id: 'easy',
    name: 'Easy',
    desc: 'Learning the dish',
    icon: '🌱',
    minWinProb: 0.50,
    maxExpands: 3,
    maxAttacks: 2,
    useMopUp: false,
    useChainBreaking: false,
    usePunishWindow: false,
    prioritizeNutrients: false,
    startingBonus: 0,
  },
  normal: {
    id: 'normal',
    name: 'Normal',
    desc: 'Fair competition',
    icon: '🍄',
    minWinProb: 0.45,
    maxExpands: 4,
    maxAttacks: 3,
    useMopUp: false,
    useChainBreaking: false,
    usePunishWindow: false,
    prioritizeNutrients: true,
    startingBonus: 0,
  },
  hard: {
    id: 'hard',
    name: 'Hard',
    desc: 'Smart & ruthless',
    icon: '☠️',
    minWinProb: 0.40,
    maxExpands: 5,
    maxAttacks: 4,
    useMopUp: true,
    useChainBreaking: true,
    usePunishWindow: true,
    prioritizeNutrients: true,
    startingBonus: 0,
  },
  insane: {
    id: 'insane',
    name: 'Insane',
    desc: 'Unfair advantage',
    icon: '💀',
    minWinProb: 0.35,
    maxExpands: 6,
    maxAttacks: 5,
    useMopUp: true,
    useChainBreaking: true,
    usePunishWindow: true,
    prioritizeNutrients: true,
    startingBonus: 3,
  },
};

// ============ SPECIES DEFINITIONS ============
export const SPECIES = {
  penicillium: {
    id: 'penicillium',
    name: 'Penicillium',
    color: 'rgba(34, 139, 134, ALPHA)',
    icon: '🟢',
    strongVs: ['aspergillus'],
    weakVs: ['rhizopus'],
    passive: 'Adaptive: +1 growth per terrain type owned',
    passiveShort: '+1g/terrain type',
    lore: 'The famous mold that changed medicine forever. In 1928, Alexander Fleming discovered that Penicillium notatum produced a substance that killed bacteria - the first antibiotic. This adaptable fungus thrives in diverse environments, from soil to decaying food, using its metabolic flexibility to outcompete other microorganisms.',
    funFact: 'Penicillium species are also responsible for the distinctive flavors in blue cheeses like Roquefort and Gorgonzola.',
  },
  aspergillus: {
    id: 'aspergillus',
    name: 'Aspergillus',
    color: 'rgba(205, 92, 52, ALPHA)',
    icon: '🟠',
    strongVs: ['rhizopus'],
    weakVs: ['penicillium'],
    passive: 'Aggressive: Roll 4 attack dice instead of 3',
    passiveShort: '4 atk dice',
    lore: 'One of the most aggressive colonizers in the fungal kingdom. Aspergillus species release clouds of spores that can travel vast distances, rapidly establishing new colonies. Some species are used in food production (soy sauce, sake), while others are dangerous pathogens that can cause severe lung infections.',
    funFact: 'Aspergillus niger is used industrially to produce citric acid - the sour component in most soft drinks.',
  },
  rhizopus: {
    id: 'rhizopus',
    name: 'Rhizopus',
    color: 'rgba(147, 51, 234, ALPHA)',
    icon: '🟣',
    strongVs: ['penicillium', 'candida'],
    weakVs: ['aspergillus'],
    passive: 'Frontier Growth: +1 growth per adjacent empty territory',
    passiveShort: '+1g/empty adj',
    lore: 'The common bread mold, known for its incredibly fast growth rate. Rhizopus spreads via root-like structures called rhizoids that anchor it while extracting nutrients. It can completely colonize a food source in just 24-48 hours, making it one of nature\'s most efficient decomposers.',
    funFact: 'Rhizopus is essential for making tempeh, the Indonesian fermented soybean cake that\'s become a global protein source.',
  },
  candida: {
    id: 'candida',
    name: 'Candida',
    color: 'rgba(236, 72, 153, ALPHA)',
    icon: '🩷',
    strongVs: ['mucor'],
    weakVs: ['rhizopus'],
    passive: 'Resilient: Immune to plague, +2 defense',
    passiveShort: 'plague immune',
    lore: 'A remarkably resilient yeast that lives in and on the human body. Candida species have evolved sophisticated defense mechanisms to survive the human immune system, forming protective biofilms and rapidly adapting to antifungal treatments. This survival expertise makes them formidable opponents.',
    funFact: 'Candida albicans can switch between yeast and filamentous forms depending on its environment - a rare ability called dimorphism.',
  },
  mucor: {
    id: 'mucor',
    name: 'Mucor',
    color: 'rgba(202, 138, 4, ALPHA)',
    icon: '🟡',
    strongVs: ['penicillium', 'aspergillus'],
    weakVs: ['candida'],
    passive: 'Toxic Aura: All territories act as toxic terrain',
    passiveShort: 'toxic aura',
    lore: 'A pin mold that produces potent chemical compounds to defend its territory. Mucor species release enzymes and metabolites that inhibit the growth of competing fungi, essentially creating a toxic zone around their colonies. This chemical warfare strategy makes them dominant in their preferred habitats.',
    funFact: 'Some Mucor species are used to produce enzymes for cheese ripening and can ferment soybeans into the Chinese delicacy sufu (fermented tofu).',
  },
  cordyceps: {
    id: 'cordyceps',
    name: 'Cordyceps',
    color: 'rgba(255, 87, 51, ALPHA)',
    icon: '🔴',
    strongVs: ['rhizopus', 'mucor'],
    weakVs: ['aspergillus'],
    passive: 'Parasitic: Conquering enemies gives +2 bonus biomass',
    passiveShort: '+2 on conquest',
    lore: 'The infamous "zombie fungus" that takes control of insect hosts, forcing them to climb to high points before releasing spores. Cordyceps has evolved remarkable mechanisms to manipulate host behavior, making it one of nature\'s most sophisticated parasites.',
    funFact: 'Cordyceps sinensis is highly prized in traditional Chinese medicine and can sell for over $20,000 per kilogram.',
  },
  stachybotrys: {
    id: 'stachybotrys',
    name: 'Stachybotrys',
    color: 'rgba(30, 30, 30, ALPHA)',
    icon: '⚫',
    strongVs: ['candida', 'penicillium'],
    weakVs: ['cordyceps'],
    passive: 'Black Death: -1 to enemy growth on adjacent territories',
    passiveShort: '-1 enemy growth',
    lore: 'Known as "black mold," Stachybotrys chartarum produces mycotoxins that are harmful to other organisms. Its dark, slimy colonies spread slowly but steadily, poisoning the surrounding environment and weakening any competitors that venture too close.',
    funFact: 'Despite its fearsome reputation, Stachybotrys requires very wet conditions and is actually quite rare compared to other household molds.',
  },
  trichoderma: {
    id: 'trichoderma',
    name: 'Trichoderma',
    color: 'rgba(34, 197, 94, ALPHA)',
    icon: '💚',
    strongVs: ['stachybotrys', 'aspergillus'],
    weakVs: ['mucor'],
    passive: 'Mycoparasite: +1 attack die vs fungal enemies',
    passiveShort: '+1 atk die',
    lore: 'A beneficial fungus that attacks and parasitizes other fungi. Trichoderma species are used in agriculture as biocontrol agents, protecting crops by outcompeting and destroying plant pathogens. They are nature\'s fungal warriors.',
    funFact: 'Trichoderma is used commercially to produce enzymes for stonewashing jeans, giving denim its faded look.',
  },
};

// ============ MUTATIONS ============
export const MUTATIONS = [
  { id: 'rapid_growth', name: 'Rapid Growth', desc: '+1 growth per territory', icon: '🌱' },
  { id: 'thick_walls', name: 'Thick Walls', desc: '+1 defender die everywhere', icon: '🧱' },
  { id: 'spore_burst', name: 'Spore Burst', desc: 'All attacks do +1 damage', icon: '💥' },
  { id: 'efficient', name: 'Efficient', desc: 'Keep +2 biomass after winning', icon: '♻️' },
  { id: 'toxic_skin', name: 'Toxic Skin', desc: 'Attackers lose 1 before combat', icon: '🧪' },
  { id: 'network', name: 'Neural Network', desc: '+5 network bonus (was +3)', icon: '🧠' },
];

// ============ EVENTS ============
export const EVENTS = [
  { id: 'drought', name: 'Drought', desc: '-1 growth everywhere', effect: 'growth', value: -1 },
  { id: 'bloom', name: 'Nutrient Bloom', desc: '+2 growth everywhere', effect: 'growth', value: 2 },
  { id: 'plague', name: 'Plague', desc: 'Territories ≤2 biomass die', effect: 'plague', value: 2 },
  { id: 'calm', name: 'Calm', desc: 'Nothing happens', effect: 'none', value: 0 },
];